import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";
import { invalidateAppSettings } from "../lib/useAppSettings";

const UOMS = ["each", "LF", "SF", "sheet"];

/** FT-4 — Settings > Scheduling. Every switch ships OFF; the countable table is a definition, not a rate. */
export function SchedulingSection() {
  const [settings, setSettings] = useState(null);
  const [learning, setLearning] = useState(null);
  const [newCode, setNewCode] = useState("");
  const reload = () => api.get("/settings/app").then((r) => setSettings(r.data)).catch(() => {});
  const loadLearning = () => api.get("/scheduling/learning").then((r) => setLearning(r.data)).catch(() => {});
  useEffect(() => { reload(); loadLearning(); }, []);
  const save = async (patch, label = "Scheduling settings saved") => {
    try {
      const r = await api.patch("/settings/app", patch);
      setSettings(r.data); invalidateAppSettings(); toast.success(label);
      if ("scheduling_actuals_learning" in patch) loadLearning();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const putDef = async (code, definition) => {
    try {
      const r = await api.put("/scheduling/countable", { scope_code: code, definition });
      setSettings((s) => ({ ...s, scheduling_countable_units: r.data.scheduling_countable_units }));
      toast.success(definition ? `${code} definition saved` : `${code} definition removed`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  if (!settings) return null;
  const table = settings.scheduling_countable_units || {};

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="settings-scheduling-section">
      <h3 className="text-sm font-semibold text-gray-900">Scheduling engine</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Minutes per countable unit come from each project's budgeted hours; the table below only says which
        products count. Everything here ships off.
      </p>
      <div className="mt-3 space-y-2">
        {[["scheduling_auto_durations", "Auto durations from unit rates",
           "room and mobilization durations = countable units × the scope's minutes per unit (instead of budgeted hours ÷ rooms); same cascade, same locks"],
          ["scheduling_actuals_learning", "Actuals learning",
           "approved hours ÷ installed countable units per scope, proposed as a company default for NEW jobs — an admin accepts; existing jobs never change"],
          ["scheduling_dependencies", "Dependency conflicts",
           "flag a successor that is not at predecessor + offset and offer one-click Shift successors (locks respected, logged); nothing moves on its own"],
          ["scheduling_rate_drift_alerts", "Rate drift alerts",
           "amber in RA when a scope's actual minutes per unit (approved hours ÷ installed units) runs over the rate the schedule was built on — flags only, never moves a date"]].map(
          ([key, label, hint]) => (
            <label key={key} className="text-xs text-gray-500 flex items-start gap-2">
              <input type="checkbox" className="mt-0.5" data-testid={`settings-${key.replace(/_/g, "-")}`}
                     checked={!!settings[key]} onChange={(e) => save({ [key]: e.target.checked })} />
              <span>{label}<span className="block text-[11px] text-gray-400">{hint}</span></span>
            </label>
          ))}
      </div>

      {settings.scheduling_rate_drift_alerts && (
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50/60 p-2"
             data-testid="settings-rate-drift-thresholds">
          {[["scheduling_rate_drift_amber_percent", "Amber at (%)", 20],
            ["scheduling_rate_drift_red_percent", "Rose at (%)", 40],
            ["scheduling_rate_drift_min_hours", "Not before (approved hours)", 40]].map(
            ([key, label, dflt]) => (
              <label key={key} className="inline-flex items-center gap-1.5 text-[11px] text-gray-600">
                {label}
                <Input type="number" min={0} step="1" defaultValue={settings[key] ?? dflt}
                       className="h-7 w-20 text-xs"
                       data-testid={`settings-${key.replace(/_/g, "-")}`}
                       onBlur={(e) => Number(e.target.value) !== Number(settings[key])
                         && save({ [key]: Number(e.target.value) }, "Rate drift thresholds saved")} />
              </label>
            ))}
          <span className="text-[11px] text-gray-400">
            A scope also has to be 15% installed before anything is flagged.
          </span>
        </div>
      )}

      <div className="mt-4">
        <div className="text-xs font-semibold text-gray-700">Countable units per scope</div>        <div className="text-[11px] text-gray-400 mb-2">A product is excluded when its text contains an exclude term; otherwise it counts, in the unit of the first matching rule (default unit if none). A job can override this on its Info tab.</div>
        <div className="space-y-2">
          {Object.entries(table).sort().map(([code, def]) => (
            <CountableRow key={code} code={code} def={def} onSave={(d) => putDef(code, d)} onRemove={() => putDef(code, null)} />
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} placeholder="Scope code (e.g. TRA)" className="h-8 w-44 text-xs" data-testid="settings-countable-new-code" />
          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!newCode.trim() || !!table[newCode.trim()]}
                  onClick={() => { putDef(newCode.trim(), { default_uom: "each", count: [], exclude: [] }); setNewCode(""); }}
                  data-testid="settings-countable-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add scope
          </Button>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-xs font-semibold text-gray-700">Company defaults (from actuals)</div>
        {!learning?.enabled ? (
          <div className="text-[11px] text-gray-400" data-testid="settings-learning-off">Turn on Actuals learning to see proposals. Accepted defaults: {Object.keys(learning?.company_rates || {}).length}</div>
        ) : learning.rows.length === 0 ? (
          <div className="text-[11px] text-gray-400" data-testid="settings-learning-empty">No approved hours against installed units yet — nothing to learn from.</div>
        ) : (
          <table className="w-full text-xs mt-1" data-testid="settings-learning-table">
            <thead className="text-[10px] uppercase text-gray-500"><tr>
              <th className="text-left py-1">Scope</th><th className="text-right">Approved h</th><th className="text-right">Installed units</th>
              <th className="text-right">Proposed min/unit</th><th className="text-right">Budgeted (avg)</th><th className="text-right">Accepted</th><th></th>
            </tr></thead>
            <tbody>
              {learning.rows.map((r) => (
                <tr key={r.scope_code} className="border-t border-gray-100" data-testid={`learning-row-${r.scope_code}`}>
                  <td className="py-1 font-medium">{r.scope_code} <span className="text-gray-400">({r.projects} job{r.projects === 1 ? "" : "s"})</span></td>
                  <td className="text-right tabular-nums">{r.approved_hours}</td>
                  <td className="text-right tabular-nums">{r.installed_units}</td>
                  <td className="text-right tabular-nums font-semibold">{r.proposed_minutes_per_unit}</td>
                  <td className="text-right tabular-nums text-gray-500">{r.budget_minutes_per_unit_avg ?? "—"}</td>
                  <td className="text-right tabular-nums">{r.accepted?.minutes_per_unit ?? "—"}</td>
                  <td className="text-right">
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`learning-accept-${r.scope_code}`}
                            onClick={async () => {
                              try { await api.post("/scheduling/learning/accept", { scope_code: r.scope_code, minutes_per_unit: r.proposed_minutes_per_unit }); toast.success(`${r.scope_code} default accepted`); loadLearning(); }
                              catch (e) { toast.error(e.response?.data?.detail || "Not accepted"); }
                            }}>
                      Accept as default
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function CountableRow({ code, def, onSave, onRemove }) {
  const [d, setD] = useState(def);
  useEffect(() => setD(def), [def]);
  const dirty = JSON.stringify(d) !== JSON.stringify(def);
  const setRule = (i, patch) => setD({ ...d, count: d.count.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  return (
    <div className="rounded-md border border-gray-200 p-2 text-xs" data-testid={`countable-row-${code}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <b className="text-gray-900">{code}</b>
        <span className="text-gray-400">default unit</span>
        <select value={d.default_uom || "each"} onChange={(e) => setD({ ...d, default_uom: e.target.value })} className="h-7 border border-gray-300 rounded px-1 bg-white" data-testid={`countable-default-uom-${code}`}>
          {UOMS.map((u) => <option key={u}>{u}</option>)}
        </select>
        <span className="text-gray-400 ml-2">exclude (comma-separated)</span>
        <Input value={(d.exclude || []).join(", ")} onChange={(e) => setD({ ...d, exclude: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
               className="h-7 w-64 text-xs" data-testid={`countable-exclude-${code}`} />
        <div className="ml-auto flex items-center gap-1">
          {dirty && <Button size="sm" className="h-7 text-xs" onClick={() => onSave(d)} data-testid={`countable-save-${code}`}>Save</Button>}
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400" onClick={onRemove} title="Remove definition" data-testid={`countable-remove-${code}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        <span className="text-gray-400">counts when text contains →</span>
        {(d.count || []).map((r, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5">
            <input value={r.match} onChange={(e) => setRule(i, { match: e.target.value })} className="w-24 bg-transparent text-xs" data-testid={`countable-match-${code}-${i}`} />
            <select value={r.uom} onChange={(e) => setRule(i, { uom: e.target.value })} className="bg-transparent text-xs">
              {UOMS.map((u) => <option key={u}>{u}</option>)}
            </select>
            <button type="button" onClick={() => setD({ ...d, count: d.count.filter((_, j) => j !== i) })} className="text-gray-400">×</button>
          </span>
        ))}
        <button type="button" onClick={() => setD({ ...d, count: [...(d.count || []), { match: "", uom: d.default_uom || "each" }] })}
                className="text-blue-700 underline" data-testid={`countable-add-rule-${code}`}>+ rule</button>
      </div>
    </div>
  );
}

/** FT-6 — Settings > GC schedule confirmations. OFF by default; the test address routes every send to one inbox. */
export function GcConfirmSection() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings/app").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const save = async (patch, label = "GC confirmation settings saved") => {
    try {
      const r = await api.patch("/settings/app", patch);
      setSettings(r.data); invalidateAppSettings(); toast.success(label);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  if (!settings) return null;
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="settings-gc-confirm-section">
      <h3 className="text-sm font-semibold text-gray-900">GC schedule confirmations</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Every scheduled mobilization inside the lead window is queued on the job with a ready email (sent as the
        assigned PM, to the GC project manager). A person reviews and sends — review before send is never bypassed.
      </p>
      <label className="mt-3 text-xs text-gray-500 flex items-start gap-2">
        <input type="checkbox" className="mt-0.5" data-testid="settings-gc-confirmation-emails"
               checked={!!settings.gc_confirmation_emails} onChange={(e) => save({ gc_confirmation_emails: e.target.checked })} />
        <span>Queue GC schedule confirmations<span className="block text-[11px] text-gray-400">off = nothing is queued or drafted</span></span>
      </label>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-[11px] text-gray-500">
          Test email address
          <Input type="email" defaultValue={settings.gc_confirmation_test_email || ""} placeholder="you@absinc.com" className="h-8 text-xs mt-0.5"
                 data-testid="settings-gc-confirmation-test-email"
                 onBlur={(e) => e.target.value.trim() !== (settings.gc_confirmation_test_email || "")
                   && save({ gc_confirmation_test_email: e.target.value.trim() }, e.target.value.trim() ? "Test mode on — every send goes to that address" : "Test mode off — sends go to the GC")} />
          <span className="block text-gray-400">While set, EVERY confirmation goes here instead of the GC, with the real recipients named in the subject. Clear it to go live.</span>
        </label>
        <label className="text-[11px] text-gray-500">
          Lead window (days before start)
          <Input type="number" min={0} max={60} defaultValue={settings.gc_confirmation_lead_days ?? 5} className="h-8 text-xs mt-0.5"
                 data-testid="settings-gc-confirmation-lead-days"
                 onBlur={(e) => Number(e.target.value) !== Number(settings.gc_confirmation_lead_days) && save({ gc_confirmation_lead_days: Number(e.target.value) }, "Lead window saved")} />
          <span className="block text-gray-400">A mobilization starting inside this window is queued until it has been confirmed for its current date.</span>
        </label>
      </div>
      {settings.gc_confirmation_emails && settings.gc_confirmation_test_email && (
        <div className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="settings-gc-confirm-test-banner">
          Test mode: confirmations are routed to <b>{settings.gc_confirmation_test_email}</b>.
        </div>
      )}
    </section>
  );
}
